import { HoverChar } from "@/components/ui/hover-char";
import { cn } from "@/utils/cn";
import { fetchTourById } from "@a/tours";
import { confirmDialog } from "@c/ds";
import { clearEditorDirty, isEditorDirty } from "@h/editor/useUnsavedGuard";
import { useBookingCounts } from "@h/useBookings";
import { useContentHealth } from "@h/useContentHealth";
import { useAuth } from "@h/useCtx";
import { useIsMobile } from "@h/useResponsive";
import { useQuery } from "@tanstack/react-query";
import { buildRoute } from "@u/routes";
import { conceptIcon } from "@u/conceptIcons";
import { planAllows } from "@u/featureGating";
import {
  tourEditorGroups,
  visibleTourSections,
  TOUR_GROUP_LABEL,
} from "@u/tourEditorNav";
import Logo from "@ui/logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@ui/sidebar";
import { useSidebar } from "@ui/sidebar-context";
import {
  ChevronLeft,
  BarChart3,
  BookOpen,
  Contact,
  CreditCard,
  Home,
  Image,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Map,
  Paintbrush,
  Settings,
  TriangleAlert,
  Users,
} from "lucide-react";
import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

// Ajustamos las clases para el estado colapsado y expandido
const COLLAPSED_BTN_CLASSES = "justify-center p-0! size-10! mx-auto";
const EXPANDED_BTN_CLASSES = "px-2 justify-start w-full";
const EXPANDED_TEXT_CLASSES = "ml-3 opacity-100 flex items-center";
const COLLAPSED_TEXT_CLASSES =
  "opacity-0 w-0 overflow-hidden ml-0 flex items-center";
const TRANSITION_CLASSES = "transition-all duration-300";
const MENU_BTN_BASE_CLASSES =
  "group/btn min-h-10 transition-all duration-300 bg-card hover:bg-muted/40";
const MENU_BTN_TEXT_CLASSES = "text-muted-foreground hover:text-foreground";
const MENU_BTN_TEXT_ACTIVE_CLASSES =
  "text-muted-foreground hover:text-foreground data-[active=true]:text-foreground";
const ICON_BASE_CLASSES = "shrink-0 transition-colors duration-300";
const ICON_ACTIVE_COLOR = "text-primary dark:text-white";
const ICON_HOVER_COLOR = "group-hover/btn:text-foreground";
const LABEL_CLASSES =
  "font-mono text-sm tracking-widest uppercase transition-transform duration-300";
const LABEL_HOVER_TRANSLATE = "group-hover/btn:translate-x-1";
const FOOTER_BTN_BASE_CLASSES =
  "group/btn h-9 transition-all duration-300 bg-transparent hover:bg-transparent";

export function AppSidebar() {
  const { signOut, isAdmin } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { open, setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const handleNavClick = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);

  // Editor-nav guard: in editor mode the sidebar's only links are back / the
  // section tabs / the management link, and each section is a separate form —
  // warn before a dirty section's edits are dropped. (BrowserRouter has no
  // useBlocker, so we intercept the click here.)
  const guardedNav = useCallback(
    async (e: React.MouseEvent, to: string) => {
      if (!isEditorDirty()) {
        handleNavClick();
        return;
      }
      e.preventDefault();
      const discard = await confirmDialog({
        variant: "warning",
        title: t("unsaved_changes_title"),
        description: t("unsaved_changes_description"),
        confirmLabel: t("discard_changes"),
        cancelLabel: t("continue_editing"),
      });
      if (!discard) return;
      clearEditorDirty();
      handleNavClick();
      navigate(to);
    },
    [t, navigate, handleNavClick],
  );
  const { data: pendingBookings } = useBookingCounts();
  const { count: healthCount, byResource: healthByResource } =
    useContentHealth();

  // Canonical: /dashboard/tours/:id. Exclude the transient legacy
  // /dashboard/tours/edit/:id (redirects to the canonical path).
  const editorMatch = location.pathname.match(
    /^\/dashboard\/tours\/(?!edit(?:\/|$))([^/]+)/,
  );
  const isEditorMode = !!editorMatch;
  const tourId = editorMatch?.[1] ?? "";

  const { data: editTour } = useQuery({
    queryKey: ["tour", tourId],
    queryFn: () => fetchTourById(tourId),
    enabled: isEditorMode && !!tourId,
    staleTime: 1000 * 60 * 5,
  });
  // "leads" section shows when it's enabled on the tour (toggle lives in the
  // features section; default on). Every tour — real-estate included — manages
  // leads/bookings/analytics per-tour.
  const leadsAllowed = editTour?.settings?.show_lead_capture !== false;
  const tourSectionGroups = tourEditorGroups(
    visibleTourSections({
      isAgency: planAllows(editTour?.owner_plan_type, "agency"),
      leadsAllowed,
    }),
  );

  // Admin user-detail rail: entering /dashboard/users/:id swaps the sidebar for
  // per-user sections (?section=), same pattern as the tour/venue editor rails.
  const userMatch = location.pathname.match(/^\/dashboard\/users\/([^/]+)/);
  const isUserDetailMode = !!userMatch;
  const userDetailId = userMatch?.[1] ?? "";
  const activeUserSection = searchParams.get("section") ?? "overview";
  const userRailItems = [
    { key: "overview", label: t("overview"), Icon: LayoutDashboard },
    { key: "tours", label: t("tours"), Icon: Map },
    { key: "media", label: t("media"), Icon: Image },
    { key: "configuracion", label: t("configuration"), Icon: Settings },
  ].map((s) => ({
    key: s.key,
    label: s.label,
    Icon: s.Icon,
    to: buildRoute.userDetail(
      userDetailId,
      s.key === "overview" ? undefined : s.key,
    ),
    isActive: activeUserSection === s.key,
  }));

  const isRailMode = isUserDetailMode;
  const railItems = userRailItems;
  const backTo = buildRoute.users();
  const backLabel = t("users");

  const navItems = [
    {
      label: t("general", "General"),
      items: [
        {
          title: t("overview"),
          path: buildRoute.overview(),
          icon: LayoutDashboard,
        },
        { title: t("tours"), path: buildRoute.tours(), icon: Map },
        {
          title: t("health.nav"),
          path: buildRoute.health(),
          icon: TriangleAlert,
          badge: healthCount || undefined,
        },
      ],
    },
    {
      // Funnel order: incoming → manage → outcome → measure.
      label: t("nav_group_clients", "Clientes"),
      items: [
        {
          title: t("crm_contacts_title"),
          path: buildRoute.contacts(),
          icon: Contact,
        },
        {
          title: t("bookings"),
          path: buildRoute.bookings(),
          icon: conceptIcon.booking,
          badge: pendingBookings || undefined,
        },
        {
          title: t("analytics"),
          path: buildRoute.analytics(),
          icon: BarChart3,
        },
      ],
    },
    {
      label: t("nav_group_org", "Organización"),
      items: [
        ...(isAdmin
          ? [{ title: t("users"), path: buildRoute.users(), icon: Users }]
          : []),
      ],
    },
    {
      label: t("system_config"),
      items: [
        { title: t("settings"), path: buildRoute.settings(), icon: Settings },
        {
          title: t("branding"),
          path: buildRoute.branding(),
          icon: Paintbrush,
        },
        {
          title: t("billing"),
          path: buildRoute.billing(),
          icon: CreditCard,
        },
        {
          title: t("help_title", "Manuales"),
          path: buildRoute.help(),
          icon: BookOpen,
        },
      ],
    },
  ].filter((group) => group.items.length > 0);

  const handleLogout = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await signOut();
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
      }
    },
    [signOut],
  );

  return (
    <Sidebar
      collapsible={isMobile ? "offcanvas" : "icon"}
      className="border-r border-border/20 z-40"
    >
      {/* HEADER */}
      <SidebarHeader
        className={cn(
          TRANSITION_CLASSES,
          open ? "px-6 pt-8 pb-6" : "p-4 flex justify-center items-center h-24",
        )}
      >
        <Logo
          variant={open ? "horizontal" : "icon"}
          theme="auto"
          size="sm"
          className={cn(open ? "w-full" : "w-8")}
        />
      </SidebarHeader>

      {/* CONTENT */}
      <SidebarContent
        className={cn(
          "px-4 py-2 transition-all duration-300",
          "group-data-[collapsible=icon]:px-2",
        )}
      >
        {isRailMode ? (
          <SidebarGroup className="py-3">
            <SidebarGroupContent className="mb-4">
              <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
                <SidebarMenuItem className="w-full">
                  <SidebarMenuButton
                    asChild
                    tooltip={backLabel}
                    size="lg"
                    className={cn(
                      MENU_BTN_BASE_CLASSES,
                      MENU_BTN_TEXT_CLASSES,
                      open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
                    )}
                  >
                    <Link
                      to={backTo}
                      onClick={handleNavClick}
                      className="flex items-center w-full h-full"
                    >
                      <ChevronLeft
                        size={18}
                        strokeWidth={1.5}
                        className="shrink-0 transition-colors duration-300 group-hover/btn:text-foreground"
                      />
                      <div
                        className={cn(
                          TRANSITION_CLASSES,
                          open ? EXPANDED_TEXT_CLASSES : COLLAPSED_TEXT_CLASSES,
                        )}
                      >
                        <span className="truncate font-mono text-sm tracking-widest uppercase transition-transform duration-300 group-hover/btn:translate-x-1">
                          {backLabel}
                        </span>
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>

            <SidebarGroupContent>
              <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
                {railItems.map((item) => {
                  const { label, isActive, to } = item;
                  return (
                    <Fragment key={item.key}>
                      <SidebarMenuItem className="w-full">
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={label}
                          size="lg"
                          className={cn(
                            `${MENU_BTN_BASE_CLASSES} data-[active=true]:bg-transparent`,
                            MENU_BTN_TEXT_ACTIVE_CLASSES,
                            open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
                          )}
                        >
                          <Link
                            to={to}
                            onClick={(e) => guardedNav(e, to)}
                            className="flex items-center w-full h-full"
                          >
                            <item.Icon
                              size={18}
                              strokeWidth={1.5}
                              className={cn(
                                ICON_BASE_CLASSES,
                                isActive ? ICON_ACTIVE_COLOR : ICON_HOVER_COLOR,
                              )}
                            />
                            <div
                              className={cn(
                                TRANSITION_CLASSES,
                                open
                                  ? EXPANDED_TEXT_CLASSES
                                  : COLLAPSED_TEXT_CLASSES,
                              )}
                            >
                              <HoverChar
                                isActive={isActive}
                                className="font-mono font-black text-sm group-hover/btn:w-3 group-hover/btn:opacity-100 text-muted-foreground mr-2 mt-0.5"
                                activeClassName="w-3 opacity-100 font-mono font-black text-sm text-primary dark:text-white mr-2 mt-0.5"
                              />
                              <span
                                className={cn(
                                  LABEL_CLASSES,
                                  isActive || LABEL_HOVER_TRANSLATE,
                                )}
                              >
                                {label}
                              </span>
                            </div>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </Fragment>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : isEditorMode ? (
          <SidebarGroup className="py-3">
            {/* Back button */}
            <SidebarGroupContent className="mb-4">
              <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
                <SidebarMenuItem className="w-full">
                  <SidebarMenuButton
                    asChild
                    tooltip={t("my_tours")}
                    size="lg"
                    className={cn(
                      MENU_BTN_BASE_CLASSES,
                      MENU_BTN_TEXT_CLASSES,
                      open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
                    )}
                  >
                    <Link
                      to={buildRoute.tours()}
                      onClick={(e) => guardedNav(e, buildRoute.tours())}
                      className="flex items-center w-full h-full"
                    >
                      <ChevronLeft
                        size={18}
                        strokeWidth={1.5}
                        className="shrink-0 transition-colors duration-300 group-hover/btn:text-foreground"
                      />
                      <div
                        className={cn(
                          TRANSITION_CLASSES,
                          open ? EXPANDED_TEXT_CLASSES : COLLAPSED_TEXT_CLASSES,
                        )}
                      >
                        <span className="font-mono text-sm tracking-widest uppercase transition-transform duration-300 group-hover/btn:translate-x-1">
                          {t("my_tours")}
                        </span>
                      </div>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>

            {tourSectionGroups.map(({ group, items }) => (
              <Fragment key={group}>
                <SidebarGroupLabel className="px-2 font-mono text-xs uppercase tracking-wider text-muted-foreground mt-4 first:mt-0 mb-3 group-data-[collapsible=icon]:hidden">
                  {t(TOUR_GROUP_LABEL[group])}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
                    {items.map((section) => {
                      const activeSection =
                        searchParams.get("section") ?? "overview";
                      const isActive = activeSection === section.key;
                      const to = buildRoute.detail(tourId, section.key);
                      const sectionBadge =
                        section.key === "avisos"
                          ? healthByResource[tourId] || undefined
                          : undefined;
                      return (
                        <SidebarMenuItem key={section.key} className="w-full">
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            tooltip={t(section.labelKey, section.key)}
                            size="lg"
                            className={cn(
                              `${MENU_BTN_BASE_CLASSES} data-[active=true]:bg-transparent`,
                              MENU_BTN_TEXT_ACTIVE_CLASSES,
                              open
                                ? EXPANDED_BTN_CLASSES
                                : COLLAPSED_BTN_CLASSES,
                            )}
                          >
                            <Link
                              to={to}
                              onClick={(e) => guardedNav(e, to)}
                              className="flex items-center w-full h-full"
                            >
                              <section.icon
                                size={18}
                                strokeWidth={1.5}
                                className={cn(
                                  ICON_BASE_CLASSES,
                                  isActive
                                    ? ICON_ACTIVE_COLOR
                                    : ICON_HOVER_COLOR,
                                )}
                              />
                              <div
                                className={cn(
                                  TRANSITION_CLASSES,
                                  open
                                    ? EXPANDED_TEXT_CLASSES
                                    : COLLAPSED_TEXT_CLASSES,
                                )}
                              >
                                <HoverChar
                                  isActive={isActive}
                                  className="font-mono font-black text-sm group-hover/btn:w-3 group-hover/btn:opacity-100 text-muted-foreground mr-2 mt-0.5"
                                  activeClassName="w-3 opacity-100 font-mono font-black text-sm text-primary dark:text-white mr-2 mt-0.5"
                                />
                                <span
                                  className={cn(
                                    LABEL_CLASSES,
                                    isActive || LABEL_HOVER_TRANSLATE,
                                  )}
                                >
                                  {t(section.labelKey, section.key)}
                                </span>
                                {sectionBadge && (
                                  <span className="ml-2 shrink-0 min-w-4.5 h-4.5 rounded-full bg-primary-strong text-primary-foreground text-xs font-mono flex items-center justify-center px-1 leading-none">
                                    {sectionBadge > 99 ? "99+" : sectionBadge}
                                  </span>
                                )}
                              </div>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </Fragment>
            ))}
          </SidebarGroup>
        ) : (
          navItems.map((group) => (
            <SidebarGroup key={group.label} className="py-3">
              <SidebarGroupLabel className="px-2 font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2 group-data-[collapsible=icon]:hidden">
                {group.label}
              </SidebarGroupLabel>

              <SidebarGroupContent>
                <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <SidebarMenuItem key={item.path} className="w-full">
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={item.title}
                          size="lg"
                          className={cn(
                            `${MENU_BTN_BASE_CLASSES} data-[active=true]:bg-transparent`,
                            MENU_BTN_TEXT_ACTIVE_CLASSES,
                            open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
                          )}
                        >
                          <Link
                            to={item.path}
                            onClick={handleNavClick}
                            className={cn("flex items-center w-full h-full")}
                          >
                            <item.icon
                              size={18}
                              strokeWidth={1.5}
                              className={cn(
                                ICON_BASE_CLASSES,
                                isActive ? ICON_ACTIVE_COLOR : ICON_HOVER_COLOR,
                              )}
                            />

                            <div
                              className={cn(
                                TRANSITION_CLASSES,
                                open
                                  ? EXPANDED_TEXT_CLASSES
                                  : COLLAPSED_TEXT_CLASSES,
                              )}
                            >
                              <HoverChar
                                isActive={isActive}
                                className="font-mono font-black text-sm group-hover/btn:w-3 group-hover/btn:opacity-100 text-muted-foreground mr-2 mt-0.5"
                                activeClassName="w-3 opacity-100 font-mono font-black text-sm text-primary dark:text-white mr-2 mt-0.5"
                              />

                              <span
                                className={cn(
                                  LABEL_CLASSES,
                                  isActive || LABEL_HOVER_TRANSLATE,
                                )}
                              >
                                {item.title}
                              </span>

                              {"badge" in item && item.badge && (
                                <span className="ml-2 shrink-0 min-w-4.5 h-4.5 rounded-full bg-primary-strong text-primary-foreground text-xs font-mono  flex items-center justify-center px-1 leading-none">
                                  {item.badge > 99 ? "99+" : item.badge}
                                </span>
                              )}
                            </div>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
      </SidebarContent>

      {/* FOOTER */}
      <SidebarFooter
        className={cn(
          "border-t border-border/20 mt-auto transition-all duration-300",
          open ? "p-4" : "p-0 py-4",
        )}
      >
        <SidebarMenu className="gap-2 group-data-[collapsible=icon]:items-center">
          <SidebarMenuItem className="flex justify-center w-full">
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip={t("support")}
              className={cn(
                FOOTER_BTN_BASE_CLASSES,
                MENU_BTN_TEXT_CLASSES,
                open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
              )}
            >
              <a
                href="mailto:support@andarlabs.com"
                className="flex items-center"
              >
                <LifeBuoy size={18} strokeWidth={1.5} className="shrink-0" />
                <div
                  className={cn(
                    TRANSITION_CLASSES,
                    open ? EXPANDED_TEXT_CLASSES : COLLAPSED_TEXT_CLASSES,
                  )}
                >
                  <HoverChar className="group-hover/btn:w-3 group-hover/btn:opacity-100 font-mono text-sm text-muted-foreground font-black mr-2 mt-0.5" />
                  <span className="font-mono text-sm tracking-widest uppercase group-hover/btn:translate-x-1 transition-transform duration-300">
                    Soporte
                  </span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem className="flex justify-center w-full">
            <SidebarMenuButton
              asChild
              size="lg"
              tooltip={t("go_home")}
              className={cn(
                FOOTER_BTN_BASE_CLASSES,
                MENU_BTN_TEXT_CLASSES,
                open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
              )}
            >
              <Link
                to="/"
                onClick={handleNavClick}
                className="flex items-center"
              >
                <Home size={18} strokeWidth={1.5} className="shrink-0" />
                <div
                  className={cn(
                    TRANSITION_CLASSES,
                    open ? EXPANDED_TEXT_CLASSES : COLLAPSED_TEXT_CLASSES,
                  )}
                >
                  <HoverChar className="group-hover/btn:w-3 group-hover/btn:opacity-100 font-mono text-sm text-muted-foreground font-black mr-2 mt-0.5" />
                  <span className="font-mono text-sm tracking-widest uppercase group-hover/btn:translate-x-1 transition-transform duration-300">
                    {t("go_home")}
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem className="flex justify-center w-full">
            <SidebarMenuButton
              size="lg"
              onClick={handleLogout}
              tooltip={t("exit")}
              className={cn(
                FOOTER_BTN_BASE_CLASSES,
                "text-muted-foreground hover:text-destructive",
                open ? EXPANDED_BTN_CLASSES : COLLAPSED_BTN_CLASSES,
              )}
            >
              <LogOut
                size={18}
                strokeWidth={1.5}
                className="shrink-0 group-hover/btn:text-destructive transition-colors"
              />
              <div
                className={cn(
                  TRANSITION_CLASSES,
                  open ? EXPANDED_TEXT_CLASSES : COLLAPSED_TEXT_CLASSES,
                )}
              >
                <HoverChar className="group-hover/btn:w-3 group-hover/btn:opacity-100 font-mono text-sm text-destructive font-black mr-2 mt-0.5" />
                <span className="font-mono text-sm tracking-widest uppercase group-hover/btn:translate-x-1 transition-transform duration-300">
                  {t("exit")}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
